/**
 * Operational periods scheduled while creating an incident. Period 1 can start
 * as the incident is created, which makes it the current period; every other
 * window becomes a planned period that the incident settings page edits.
 */

export const MAX_SCHEDULED_OPERATIONAL_PERIODS = 4;

export type OperationalSchedulePeriod = {
  /** 1 through 4, in order. */
  index: number;
  /** ISO time. Empty when period 1 starts at incident creation. */
  start: string;
  end: string;
};

export type OperationalSchedule = {
  startFirstOnCreate: boolean;
  /** Advance to each planned period when its start time arrives. */
  autoAdvance?: boolean;
  periods: OperationalSchedulePeriod[];
};

export function createDefaultOperationalSchedule(): OperationalSchedule {
  return {
    startFirstOnCreate: false,
    autoAdvance: true,
    periods: [{ index: 1, start: '', end: '' }],
  };
}

function parseInstant(value: string | null | undefined): number | null {
  const text = value?.trim() ?? '';
  if (!text) return null;
  const time = Date.parse(text);
  return Number.isFinite(time) ? time : null;
}

export function operationalScheduleErrors(
  schedule: OperationalSchedule | undefined
): string[] {
  const value = schedule ?? createDefaultOperationalSchedule();
  const periods = value.periods ?? [];
  const errors: string[] = [];
  if (periods.length < 1) {
    errors.push('Schedule at least one operational period.');
  }
  if (periods.length > MAX_SCHEDULED_OPERATIONAL_PERIODS) {
    errors.push('Schedule at most four operational periods.');
  }
  let previousEnd: number | null = null;
  periods.forEach((period, offset) => {
    const label = `Operational period ${offset + 1}`;
    const startsOnCreate = offset === 0 && value.startFirstOnCreate;
    const start = startsOnCreate ? null : parseInstant(period.start);
    const end = parseInstant(period.end);
    if (!startsOnCreate && start == null) {
      errors.push(`${label} start is required.`);
    }
    if (end == null) {
      errors.push(`${label} stop is required.`);
    }
    if (start != null && end != null && end <= start) {
      errors.push(`${label} must stop after it starts.`);
    }
    if (start != null && previousEnd != null && start < previousEnd) {
      errors.push(
        `${label} must start when the previous period stops or later.`
      );
    }
    if (end != null) previousEnd = end;
  });
  return errors;
}

/** The `operational_schedule` body sent with create incident. */
export function operationalSchedulePayload(
  schedule: OperationalSchedule | undefined
): {
  startFirstOnCreate: boolean;
  autoAdvance: boolean;
  periods: { start: string | null; end: string | null }[];
} {
  const value = schedule ?? createDefaultOperationalSchedule();
  return {
    startFirstOnCreate: value.startFirstOnCreate,
    autoAdvance: value.autoAdvance ?? true,
    periods: value.periods
      .slice(0, MAX_SCHEDULED_OPERATIONAL_PERIODS)
      .map((period, offset) => ({
        start:
          offset === 0 && value.startFirstOnCreate
            ? null
            : period.start.trim() || null,
        end: period.end.trim() || null,
      })),
  };
}
